// React and hooks
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

// Third party
import debounce from "lodash/debounce";

// API interfaces and types
import {
  EmailTemplateI,
  AttachmentI,
  SendEmailRequestParamsI,
} from "@/api/routes/email";
import { EmailSectionExitTypeI } from "./interface";

// Constants
import { EMAIL_SECTION_EXIT_OPTIONS } from "./constants";
import { WIDGETS } from "@/constants/widgets";
import { CUSTOM_EVENTS } from "@/constants/custom-events";

// Components
import { EmailForm } from "@/modules/email-templates-sidebar/content/v2/sections/email-form-section/email-form";
import { emailSectionActions } from "./actions-section";
import { widgets } from "@/components/shared/widgets";

// Hooks and context
import {
  handleEmailGenerationError,
  useAccountEmailAIGeneration,
  useEmailTemplatesManager,
  useEmailTemplateSSDrafts,
  EMAIL_TEMPLATE_LOCAL_DRAFTS_KEY_TYPES,
} from "@/hooks/use-email-templates-manager";
import { useQuerySendEmail } from "@/components/modules/email-templates-sidebar/queries";
import {
  AccountDetailsContextI,
  useAccountDetailsContext,
} from "@/modules/pipeline/account-details/context";
import {
  useCallingContext,
  useInCallContext,
} from "@/hooks/dialer/use-dialer-context";
import { EmailFormFormattedDataI } from "@/modules/email-templates-sidebar/interfaces";
import { LOG_CATEGORIES } from "@/constants/logs";
import { dd } from "@/helpers/datadog";

export interface EmailSectionContentPropsI {
  subject?: string;
  body?: string;
  emailId?: string;
  accountExecutiveEmail?: string;
}

export const EmailSectionContent: FC<EmailSectionContentPropsI> = ({
  subject,
  body,
  emailId,
  accountExecutiveEmail,
}) => {
  const [isEmailDataInitialized, setIsEmailDataInitialized] = useState(false);

  const accountDetailsContext = useAccountDetailsContext();
  const {
    setFocusedContact,
    accountHistoryData: { reloadData: reloadAccountHistory },
    setIsEmailSection,
  } = accountDetailsContext;
  const AccountDetailsRef = useRef<AccountDetailsContextI>(
    accountDetailsContext
  );
  useEffect(() => {
    AccountDetailsRef.current = accountDetailsContext;
  }, [accountDetailsContext]);

  const { clear: clearInCallContext } = useInCallContext();
  const { setCall, call } = useCallingContext();

  const draftId = useMemo(
    () => call?.customParameters.get("call_id") as string,
    [call]
  );

  const {
    templates: templatesData,
    activeTemplate,
    activeTemplateId,
    emailGenerationConfig,
    campaignAttachmentsData,
  } = useEmailTemplatesManager();

  const {
    isAIGenerationComplete,
    isAIGenerationStarted,
    isEmailSent,
    emailData,
    handleComposeWithAI,
    setEmailData,
    setIsEmailSent,
  } = useAccountEmailAIGeneration(activeTemplate as EmailTemplateI);

  const { setLocalDraft, getLocalDraft } = useEmailTemplateSSDrafts(
    EMAIL_TEMPLATE_LOCAL_DRAFTS_KEY_TYPES.CALL_ID
  );

  const endCallCycle = () => {
    setCall(undefined);
    clearInCallContext();
    setIsEmailSection?.(false);
  };

  useEffect(() => {
    if (!isEmailDataInitialized) {
      const templates = templatesData?.data?.email_templates;
      const onePagerTemplate = templates?.find((t) => t.name === "One Pager");
      const firstTemplateId = onePagerTemplate || templates?.[0]?.id;

      const emailDraft = getLocalDraft(draftId);

      if (firstTemplateId) {
        if (emailDraft) {
          setEmailData(emailDraft);
        } else {
          setEmailData({ subject, body });
          setLocalDraft(draftId, {
            subject,
            body,
          });
        }
        setIsEmailDataInitialized(true);
      }
    }
  }, [templatesData]);

  const [exitType, setExitType] = useState<EmailSectionExitTypeI>();

  const { mutateAsync: sendEmailAsync, isPending: isSendEmailPending } =
    useQuerySendEmail();

  const onEmailSuccess = () => {
    setIsEmailSent(true);
    reloadAccountHistory();
    setFocusedContact(undefined);
  };

  const handlerSendEmail = async (data: EmailFormFormattedDataI) => {
    if (emailGenerationConfig) {
      const { focusedContact, campaign, account } = AccountDetailsRef.current;
      const { max_follow_ups, follow_up_interval_days } =
        getLocalDraft(draftId) || {};

      const optionalParams: Partial<SendEmailRequestParamsI> = {};

      if (emailId) {
        optionalParams.email_id = emailId;
      }

      if (max_follow_ups && follow_up_interval_days) {
        optionalParams.max_follow_ups = max_follow_ups;
        optionalParams.follow_up_interval_days = follow_up_interval_days;
      }

      const parameters = {
        ...data,
        ...optionalParams,

        contact_id: focusedContact?.id as string,
        account_id: account?.id as string,
        campaign_id: campaign?.id as string,
      };

      const resp = await sendEmailAsync({
        parameters,
      });

      if (resp.status === 200) {
        dd.log(
          `${LOG_CATEGORIES.FOLLOW_UP_EMAIL}[EMAIL_SECTION] - email sent successfully`,
          {
            data: {
              responseData: resp.data,
              requestParams: parameters,
            },
          }
        );

        onEmailSuccess();
        return true;
      }

      dd.error(
        `${LOG_CATEGORIES.FOLLOW_UP_EMAIL}[EMAIL_SECTION] - email failed to send`,
        {
          data: {
            response: resp,
            requestParams: parameters,
          },
        }
      );
      handleEmailGenerationError(resp, "Failed to send email");
    }

    return false;
  };

  const debouncedHandleSendEmail = debounce(handlerSendEmail, 1000);

  //TODO find the reason of double calls to remove this throttle
  const handleEmailSend = useCallback(debouncedHandleSendEmail, [
    emailGenerationConfig,
    emailId,
  ]);

  useEffect(() => {
    if (isEmailSent && exitType) {
      endCallCycle();

      //close section based on selected exit type
      if (exitType === EMAIL_SECTION_EXIT_OPTIONS.STAY) {
        setIsEmailSection?.(false);
      }

      if (exitType === EMAIL_SECTION_EXIT_OPTIONS.CONTINUE_DIALING) {
        widgets.close({ id: WIDGETS.MAXED_DIALER });
      }
    }
  }, [isEmailSent, exitType]);

  const handleSelectExitType = (exitType: EmailSectionExitTypeI) => {
    setExitType(exitType);
  };

  const handleOnChangeSubject = (subject?: string) => {
    if (activeTemplateId) {
      const draft = getLocalDraft(draftId);
      setLocalDraft(draftId, { ...draft, subject });
    }
  };

  const handleOnChangeBody = (body?: string) => {
    if (activeTemplateId) {
      const draft = getLocalDraft(draftId);
      setLocalDraft(draftId, { ...draft, body });
    }
  };

  const handleOnAttachmentChange = (attachments: AttachmentI[]) => {
    if (activeTemplateId) {
      const draft = getLocalDraft(draftId);
      setLocalDraft(draftId, {
        ...draft,
        attachments,
      });
    }
  };

  /**
   * If email is sent from email templates sidebar
   * we need to cleanup the call and remove email section
   * from dialer toolkit
   */
  useEffect(() => {
    const eventHandler = () => {
      endCallCycle();
    };

    window.document.addEventListener(
      CUSTOM_EVENTS.ACCOUNT.EMAIL_TEMPLATES.ON_EMAIL_SEND,
      eventHandler as EventListener
    );

    return () => {
      window.document.removeEventListener(
        CUSTOM_EVENTS.ACCOUNT.EMAIL_TEMPLATES.ON_EMAIL_SEND,
        eventHandler as EventListener
      );
    };
  }, []);

  return (
    <section>
      {activeTemplate && emailGenerationConfig && (
        <EmailForm
          //  key is used to force entire component to re-render when template changes so that all form fields can be reset
          key={activeTemplateId}
          template={activeTemplate}
          senderEmail={emailGenerationConfig?.from_email}
          attachmentOptions={campaignAttachmentsData?.attachments}
          initialSubject={emailData?.subject}
          initialBody={emailData?.body}
          initialRecipientsCc={
            accountExecutiveEmail ? [accountExecutiveEmail] : []
          }
          onComposeWithAI={handleComposeWithAI}
          onSend={handleEmailSend}
          isSendingEmail={isSendEmailPending}
          isSendEmailSuccess={isEmailSent}
          isAIGenerationComplete={isAIGenerationComplete}
          isAIGenerating={isAIGenerationStarted}
          hideTitle
          hideIntro
          hideAiGenerateButton
          onChangeSubject={handleOnChangeSubject}
          onChangeBody={handleOnChangeBody}
          onChangeAttachments={handleOnAttachmentChange}
          actionsSection={emailSectionActions({
            className: "mb-3 mt-5",
            onSelect: handleSelectExitType,
            isLoading:
              (isAIGenerationStarted && !isAIGenerationComplete) ||
              isSendEmailPending,
          })}
        />
      )}
    </section>
  );
};
